import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatEUR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang, tr } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Landmark, RefreshCw, Link2, CheckCircle2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import Accounts from "@/pages/Accounts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Bank() {
  const [sp, setSp] = useSearchParams();
  const { hasPermission: hp } = useAuth();
  const canTx = hp("bank.view"), canAcc = hp("accounts.view");
  const tab = sp.get("tab") === "accounts" || !canTx ? "accounts" : "tx";
  if (!canTx && !canAcc) return null;
  return (
    <div className="space-y-6" data-testid="bank-shell">
      <Tabs value={tab} onValueChange={(v) => setSp(v === "accounts" ? { tab: "accounts" } : {})}><TabsList data-testid="bank-tabs">{canTx && <TabsTrigger value="tx" data-testid="bank-tab-tx">{tr("bankTabTx")}</TabsTrigger>}{canAcc && <TabsTrigger value="accounts" data-testid="bank-tab-accounts">{tr("bankTabAccounts")}</TabsTrigger>}</TabsList></Tabs>
      {tab === "accounts" ? <Accounts /> : <BankTransactions />}
    </div>
  );
}

function BankTransactions() {
  const { t, meta } = useLang();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.role === "owner" || user?.role === "super_admin";
  const [filter, setFilter] = useState("all");
  const [matchTx, setMatchTx] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const { data: status } = useQuery({ queryKey: ["bank-status"], queryFn: () => api.get("/bank/status").then((r) => r.data) });
  const { data: accounts = [] } = useQuery({ queryKey: ["bank-accounts"], queryFn: () => api.get("/bank/accounts").then((r) => r.data), enabled: !!status?.enabled && isOwner, retry: false });
  const { data: txs = [] } = useQuery({ queryKey: ["bank-tx", filter], queryFn: () => api.get(`/bank/transactions?only=${filter}`).then((r) => r.data), enabled: !!status?.enabled });
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: () => api.get("/invoices").then((r) => r.data), enabled: !!matchTx });

  const refreshAll = () => { qc.invalidateQueries({ queryKey: ["bank-status"] }); qc.invalidateQueries({ queryKey: ["bank-tx"] }); qc.invalidateQueries({ queryKey: ["bank-accounts"] }); qc.invalidateQueries({ queryKey: ["invoices"] }); };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/bank/sync");
      toast.success(`${t("bankSynced")} · ${data.new} ${t("bankNew")} · ${data.matched} ${t("bankMatched")}`);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const doMatch = async (inv) => {
    try {
      await api.post(`/bank/transactions/${matchTx.ponto_id}/match/${inv.id}`);
      toast.success(`${t("bankMatchedTo")} ${inv.invoice_number}`);
      setMatchTx(null); refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unpaid = invoices.filter((i) => i.status !== "paid");

  return (
    <div className="space-y-8" data-testid="bank-page">
      <div className="page-head flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{t("bankKicker")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight">{t("bankTitle")}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{t("bankSubtitle")}</p>
        </div>
        {isOwner && status?.enabled && (
          <Button onClick={sync} disabled={syncing} className="rounded-full bg-primary hover:bg-primary/90" data-testid="bank-sync-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? t("loading") : t("bankSync")}
          </Button>
        )}
      </div>

      {!status?.enabled && (
        <Card className="p-6 border-border" data-testid="bank-not-configured">
          <div className="flex items-center gap-3 text-muted-foreground"><Landmark className="h-5 w-5" /> {t("intBankOff")}</div>
        </Card>
      )}

      {status?.enabled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accounts.map((a) => (
            <Card key={a.id} className="p-6 border-border" data-testid={`bank-account-${a.iban}`}>
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{a.name}</div>
              <div className="font-display text-3xl font-bold tabular-nums mt-1">{formatEUR(a.balance || 0)}</div>
              <div className="text-xs font-mono text-muted-foreground mt-1">{a.iban}</div>
            </Card>
          ))}
          <Card className="p-6 border-border" data-testid="bank-last-run-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{t("bankLastCheck")}</div>
            <div className="font-display text-xl font-bold mt-1" data-testid="bank-last-run">{status.last_run_at ? new Date(status.last_run_at).toLocaleString(meta.locale, { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">{status.last_run_at ? `${t("bankNew")} ${status.last_run_new ?? 0} · ${t("bankMatched")} ${status.last_run_matched ?? 0} · ${status.last_run_by === "cron" ? t("bankAuto") : status.last_run_by || ""}` : t("bankNoRunYet")}</div>
            {status.last_run_error ? <div className="text-xs text-rose-600 mt-1 font-semibold" data-testid="bank-last-error">{t("bankError")}: {status.last_run_error}</div> : <div className="text-xs text-emerald-600 mt-1" data-testid="bank-ok">{t("bankAutoEveryMinute")}</div>}
            {status.unmatched_open > 0 && <div className="text-xs text-amber-700 mt-1" data-testid="bank-unmatched-count">{status.unmatched_open} {t("bankUnmatchedOpen")}</div>}
          </Card>
          <Card className="p-6 border-border">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{t("bankLastSync")}</div>
            <div className="font-display text-xl font-bold mt-1">{status.last_sync ? new Date(status.last_sync).toLocaleString(meta.locale, { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">{status.count} {t("bankTransactions").toLowerCase()}</div>
          </Card>
        </div>
      )}

      {status?.enabled && (
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <h3 className="font-display text-xl font-bold">{t("bankTransactions")}</h3>
            <div className="flex gap-1">
              {["all", "unmatched", "matched"].map((f) => (
                <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className="rounded-full h-7 text-[11px]" onClick={() => setFilter(f)} data-testid={`bank-filter-${f}`}>{t(`bankFilter_${f}`)}</Button>
              ))}
            </div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("date")}</TableHead><TableHead>{t("bankCounterpart")}</TableHead><TableHead>{t("description")}</TableHead>
              <TableHead className="text-right">{t("amount")}</TableHead><TableHead>{t("status")}</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {txs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">{t("bankNoTx")}</TableCell></TableRow>}
              {txs.map((x) => (
                <TableRow key={x.ponto_id} data-testid={`bank-tx-${x.ponto_id}`}>
                  <TableCell className="font-mono text-xs">{x.date}</TableCell>
                  <TableCell className="font-medium">{x.counterpart || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md truncate" title={x.text}>{(x.text || "").replace(/\s+/g, " ").slice(0, 90)}</TableCell>
                  <TableCell className={`text-right font-mono tabular-nums ${x.amount > 0 ? "text-emerald-600" : ""}`}>
                    {x.amount > 0 ? <ArrowDownLeft className="inline h-3 w-3 mr-1" /> : <ArrowUpRight className="inline h-3 w-3 mr-1" />}{formatEUR(x.amount)}
                  </TableCell>
                  <TableCell>
                    {x.matched_invoice_id ? <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />{x.matched_invoice_number}</Badge>
                      : x.amount > 0 ? <Badge variant="outline">{t("bankOpen")}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {isOwner && !x.matched_invoice_id && x.amount > 0 && (
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => setMatchTx(x)} data-testid={`bank-match-${x.ponto_id}`}><Link2 className="h-3 w-3 mr-1" />{t("bankLink")}</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!matchTx} onOpenChange={(v) => { if (!v) setMatchTx(null); }}>
        <DialogContent className="max-w-lg" data-testid="bank-match-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("bankLinkTitle")} · {matchTx && formatEUR(matchTx.amount)}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {unpaid.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">{t("dashNoUnpaid")}</div>}
            {unpaid.map((i) => (
              <button key={i.id} onClick={() => doMatch(i)} className="w-full flex items-center justify-between p-3 rounded-md border border-border hover:border-primary/60 text-left" data-testid={`bank-match-inv-${i.invoice_number}`}>
                <div><div className="font-mono text-sm">{i.invoice_number}</div><div className="text-xs text-muted-foreground">{i.customer_name}</div></div>
                <div className={`font-mono tabular-nums ${matchTx && Math.abs(i.total - matchTx.amount) < 0.005 ? "text-emerald-600 font-bold" : ""}`}>{formatEUR(i.total)}</div>
              </button>
            ))}
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setMatchTx(null)}>{t("cancel")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
